import { useEffect, useState } from 'react';
import { Icon } from '../../components/Icon';
import { ApiError, api, AssignmentSyncResult, CalendarTerms, CourseImportResult } from '../../lib/api';

export function CalendarPage() {
  const [ready, setReady] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [terms, setTerms] = useState<Record<string, string>>({});
  const [term, setTerm] = useState('');
  const [working, setWorking] = useState<'courses' | 'assignments' | ''>('');
  const [result, setResult] = useState<{ tone: 'ok' | 'error'; text: string }>();
  const [frameVersion, setFrameVersion] = useState(0);
  const [guest, setGuest] = useState(false);

  useEffect(() => {
    let active = true;
    void Promise.allSettled([
      api('/auth/me'),
      api<{ data: CalendarTerms }>('/api/v1/calendar/terms'),
    ]).then(([authResult, termsResult]) => {
      if (!active) return;
      // 游客模式：未登录不再强制跳转登录页，改为只读浏览（日历内容为空，写操作提示登录）
      if (authResult.status === 'rejected' && authResult.reason instanceof ApiError && authResult.reason.status === 401) {
        setGuest(true);
      }
      if (termsResult.status === 'fulfilled') {
        const available = termsResult.value.data.terms || {};
        setTerms(available);
        setTerm(Object.keys(available).sort().at(-1) || '');
      }
      setReady(true);
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!drawerOpen) return;
    const close = (event: KeyboardEvent) => { if (event.key === 'Escape' && !working) setDrawerOpen(false); };
    document.addEventListener('keydown', close);
    return () => document.removeEventListener('keydown', close);
  }, [drawerOpen, working]);

  const importCourses = async () => {
    if (!term || working) return;
    setWorking('courses'); setResult(undefined);
    try {
      const response = await api<{ data: CourseImportResult }>('/api/v1/calendar/course-import', { method: 'POST', body: JSON.stringify({ term }) });
      const data = response.data;
      const unscheduled = data.unscheduled.length ? `；${data.unscheduled.length} 门没有固定时段` : '';
      setResult({ tone: 'ok', text: `已新增 ${data.created} 个课表日程，跳过 ${data.skipped} 个重复项${unscheduled}。` });
      if (data.created) setFrameVersion((value) => value + 1);
    } catch (error) {
      setResult({ tone: 'error', text: error instanceof Error ? error.message : '课表导入失败' });
    } finally { setWorking(''); }
  };

  const syncAssignments = async () => {
    if (working) return;
    setWorking('assignments'); setResult(undefined);
    try {
      const response = await api<{ data: AssignmentSyncResult }>('/api/v1/inbox/assignments', { method: 'POST' });
      const data = response.data;
      setResult({ tone: 'ok', text: `教学平台返回 ${data.fetched} 项作业，${data.delivered} 项已进入收件箱，跳过 ${data.skipped} 项重复内容。` });
      if (data.delivered) setFrameVersion((value) => value + 1);
    } catch (error) {
      setResult({ tone: 'error', text: error instanceof Error ? error.message : '作业同步失败' });
    } finally { setWorking(''); }
  };

  return <div className="calendar-page">
    {guest ? <div className="calendar-guest-banner" role="status" style={{ position: 'absolute', left: '50%', bottom: 18, transform: 'translateX(-50%)', zIndex: 5, display: 'flex', gap: 10, alignItems: 'center', padding: '10px 16px', borderRadius: 999, background: 'rgba(28,26,23,.92)', color: '#f4efe6', fontSize: 13, boxShadow: '0 8px 28px rgba(0,0,0,.28)' }}><b>游客浏览</b><span style={{ opacity: 0.75 }}>登录后可保存日程与待办</span><a href="/login?next=%2Fcalendar" style={{ color: '#f0b429', textDecoration: 'none', fontWeight: 600 }}>登录</a></div> : null}
    {ready
      ? <iframe
          key={frameVersion}
          className="calendar-frame"
          src={`/calendar.html?embed=1${window.location.hash === '#todos' ? '#todos' : ''}`}
          title="菜根日历工作区"
          allow="clipboard-read; clipboard-write"
        />
      : <div className="calendar-loading" role="status"><span>根</span><p>正在连接你的日历上下文…</p></div>}
    {ready ? <button className="calendar-import-trigger" type="button" onClick={() => setDrawerOpen(true)}><Icon name="calendar" /><span>课程 / 作业</span></button> : null}
    <button className={`calendar-import-backdrop${drawerOpen ? ' is-open' : ''}`} aria-label="关闭导入面板" tabIndex={drawerOpen ? 0 : -1} onClick={() => { if (!working) setDrawerOpen(false); }} />
    <aside className={`calendar-import-drawer${drawerOpen ? ' is-open' : ''}`} aria-hidden={!drawerOpen} aria-label="导入课表与作业">
      <header><div><small>CALENDAR · IMPORT</small><h2>把课程带回日历</h2><p>课表写入日历；作业先进入通知收件箱。</p></div><button type="button" aria-label="关闭" disabled={Boolean(working)} onClick={() => setDrawerOpen(false)}><Icon name="close" /></button></header>
      <section className="calendar-import-section"><span className="calendar-import-index">课</span><div><small>COURSE SCHEDULE</small><h3>导入本学期课表</h3><p>按教学周展开为真实日程；已经存在的课程不会重复写入。</p><label>选择学期<select value={term} onChange={(event) => setTerm(event.target.value)} disabled={Boolean(working)}>{Object.entries(terms).map(([value, start]) => <option key={value} value={value}>{value} · 第 1 周 {start}</option>)}</select></label><button className="calendar-import-action" type="button" disabled={!term || Boolean(working)} onClick={importCourses}>{working === 'courses' ? '正在导入…' : terms[term] ? `导入从 ${terms[term]} 开始的课表` : '暂无可导入学期'}</button></div></section>
      <section className="calendar-import-section"><span className="calendar-import-index assignment">作</span><div><small>ASSIGNMENT INBOX</small><h3>同步教学平台作业</h3><p>从 selearning 与智慧南雍读取作业，只投递到收件箱，由你确认是否转成待办。</p><button className="calendar-import-action secondary" type="button" disabled={Boolean(working)} onClick={syncAssignments}>{working === 'assignments' ? '正在连接教学平台…' : '同步作业到收件箱'}</button></div></section>
      {result ? <p className={`calendar-import-result ${result.tone}`} role="status">{result.text}</p> : null}
      <footer>所有导入操作按当前登录账户隔离；同步失败不会修改现有日历。</footer>
    </aside>
  </div>;
}
